import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { setState } from './userStore';

export function useAuth() {
  const [user, setUser]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [authEvent, setAuthEvent] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) await loadProfile(session.user.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) loadProfile(session.user.id);
      if (event === 'PASSWORD_RECOVERY') setAuthEvent('PASSWORD_RECOVERY');
    });

    return () => subscription.unsubscribe();
  }, []);

  async function loadProfile(userId) {
    const [{ data }, { data: meds }] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', userId).single(),
      supabase.from('medications').select('*').eq('user_id', userId).order('created_at', { ascending: true }),
    ]);
    if (data) {
      setState({
        carrier:     data.insurance_carrier,
        planType:    data.insurance_plan_type,
        memberId:    data.member_id,
        groupNum:    data.group_num,
        planName:    data.plan_name,
        accountType: data.account_type,
        employerId:  data.employer_id,
        firstName:          data.first_name,
        lastName:           data.last_name,
        zipCode:            data.zip_code,
        dob:                data.dob,
        age:                data.age,
        gender:             data.gender,
        lastWellnessVisit:  data.last_wellness_visit || '',
      });
    }
    if (meds) setState({ medications: meds });

    // Check for a triage session within the past 48 hours that hasn't been actioned
    const fortyEightHours = 48 * 60 * 60 * 1000;
    const { data: triageSessions } = await supabase
      .from('triage_sessions')
      .select('id, care_level, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1);
    if (triageSessions && triageSessions[0]) {
      const session = triageSessions[0];
      const ms = Date.now() - new Date(session.created_at).getTime();
      const NUDGE_LEVELS = { care_today: 'urgent', care_soon: 'soon', home_care: 'home' };
      const nudgeLevel = NUDGE_LEVELS[session.care_level];
      if (ms < fortyEightHours && nudgeLevel) {
        // Guard: skip if user already actioned this session (yes or no)
        const { data: outcome } = await supabase
          .from('care_outcomes')
          .select('id')
          .eq('triage_session_id', session.id)
          .limit(1)
          .maybeSingle();
        if (!outcome) {
          setState({
            recentSymptomLevel:    nudgeLevel,
            recentSymptomAt:       session.created_at,
            recentTriageSessionId: session.id,
          });
        }
      }
    }
  }

  async function saveProfile(updates) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const row = { id: session.user.id };
    if (updates.carrier    !== undefined) row.insurance_carrier   = updates.carrier;
    if (updates.planType   !== undefined) row.insurance_plan_type = updates.planType;
    if (updates.memberId   !== undefined) row.member_id           = updates.memberId;
    if (updates.groupNum   !== undefined) row.group_num           = updates.groupNum;
    if (updates.planName   !== undefined) row.plan_name           = updates.planName;
    if (updates.firstName  !== undefined) row.first_name          = updates.firstName;
    if (updates.lastName   !== undefined) row.last_name           = updates.lastName;
    if (updates.zipCode    !== undefined) row.zip_code            = updates.zipCode;
    if (updates.dob        !== undefined) row.dob                 = updates.dob;
    if (updates.age               !== undefined) row.age                  = updates.age;
    if (updates.gender            !== undefined) row.gender               = updates.gender;
    if (updates.lastWellnessVisit !== undefined) row.last_wellness_visit  = updates.lastWellnessVisit;
    await supabase.from('profiles').upsert(row);
  }

  async function saveSymptomSession(chips, detail, answers, resultLevel, icd10 = null, conditionLabel = null) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    await supabase.from('symptom_sessions').insert({
      user_id:         session.user.id,
      chips,
      detail,
      answers,
      result_level:    resultLevel,
      icd10:           icd10 || null,
      condition_label: conditionLabel || null,
    });
  }

  async function addMedication(fields) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const { data, error } = await supabase
      .from('medications')
      .insert({ user_id: session.user.id, ...fields })
      .select()
      .single();
    if (!error && data) {
      const { medications } = (await import('./userStore')).getState();
      setState({ medications: [...(medications || []), data] });
    }
  }

  async function removeMedication(id) {
    await supabase.from('medications').delete().eq('id', id);
    const { medications } = (await import('./userStore')).getState();
    setState({ medications: (medications || []).filter(m => m.id !== id) });
  }

  async function signOut() {
    await supabase.auth.signOut();
    setState({
      carrier: null, planType: null, memberId: null, groupNum: null, planName: null,
      accountType: null, employerId: null, firstName: null, lastName: null,
      zipCode: null, dob: null, age: null, gender: null,
      acquisitionSource: null, lastWellnessVisit: null,
      recentSymptomLevel: null, recentSymptomAt: null, recentTriageSessionId: null,
      medications: [],
    });
  }

  function clearAuthEvent() { setAuthEvent(null); }

  return { user, loading, saveProfile, saveSymptomSession, signOut, addMedication, removeMedication, authEvent, clearAuthEvent };
}